import { gatedFetch } from '../utils/requestGate';

const PROD_API = 'https://api.ohbellezaoficial.com';
const RAW_API = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const API_BASE = RAW_API || (process.env.NODE_ENV === 'production' ? PROD_API : '');
/** En local, sin REACT_APP_API_URL el proxy de CRA a veces no sirve /imagenes → API directo. */
const IMAGE_BASE =
  API_BASE ||
  (process.env.NODE_ENV !== 'production' ? 'http://localhost:8081' : PROD_API);
const DEFAULT_TIMEOUT_MS = 25000;
const TOKEN_KEY = 'oh_vendedor_token';

let _vendedorId = null;
let _token = null;

export function setApiVendedorId(id) {
  _vendedorId = id ? Number(id) : null;
}

export function setAuthToken(token) {
  _token = token ? String(token) : null;
  try {
    if (_token) sessionStorage.setItem(TOKEN_KEY, _token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthToken() {
  if (_token) return _token;
  try {
    _token = sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    _token = null;
  }
  return _token;
}

export function clearAuthToken() {
  _token = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function vendorQuery(extra = {}) {
  const params = new URLSearchParams();
  Object.entries(extra).forEach(([k, v]) => {
    if (v != null && v !== '') params.set(k, String(v));
  });
  if (_vendedorId) params.set('vendedor_id', String(_vendedorId));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, headers, ...fetchOpts } = options;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let res;
  try {
    res = await gatedFetch(`${API_BASE}/api${path}`, {
      ...fetchOpts,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(headers || {}) },
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Revisa la conexión e intenta de nuevo.');
    }
    throw new Error(
      'No se pudo conectar con el servidor. Revisa el WiFi de la tablet y que el API esté Running en cPanel.'
    );
  } finally {
    if (timer) clearTimeout(timer);
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fallback =
      res.status >= 500
        ? `Error del servidor (${res.status}). Intenta de nuevo.`
        : res.status === 404 && /\/sedes/.test(path)
          ? 'El API del hosting no tiene sedes (falta actualizar/reiniciar).'
          : `Error en la solicitud (${res.status})`;
    let msg = String(json.error || fallback);
    // Nunca mostrar SQL crudo de MySQL en toasts
    if (/Data too long for column|ER_DATA_TOO_LONG|ER_DUP_ENTRY|Duplicate entry|ER_|SQLSYNTAX|mysql/i.test(msg)) {
      msg = /Data too long|ER_DATA_TOO_LONG/i.test(msg)
        ? 'Uno de los datos es demasiado largo. Si el error continúa, el API del hosting necesita actualizarse y reiniciarse.'
        : /Duplicate|ER_DUP/i.test(msg)
          ? 'Ya existe un registro similar. Intenta de nuevo.'
          : res.status >= 500
            ? fallback
            : 'No se pudo completar la acción. Intenta de nuevo.';
    }
    const error = new Error(msg);
    error.status = res.status;
    error.code = json.code;
    throw error;
  }
  return json;
}

export function imageUrl(imagen) {
  if (!imagen) return null;
  if (imagen.startsWith('http')) return imagen;
  const base = IMAGE_BASE || '';
  return `${base}/${imagen.replace(/^\//, '')}`;
}

export function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

export const api = {
  ping: () => request('/ping'),
  login: async (username, password) => {
    const res = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        username: String(username || '').trim(),
        password: String(password || ''),
        app: 'vendedor',
      }),
    });
    if (res.data?.token) setAuthToken(res.data.token);
    return res;
  },
  me: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }).finally(() => clearAuthToken()),
  getDashboard: () => request(`/dashboard${vendorQuery()}`),
  getCategorias: () => request('/categorias'),
  getProductos: ({ q = '', categoria = '', subcategoria = '', offset = 0, limit = 48 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (categoria && categoria !== 'Todas') params.set('categoria', categoria);
    if (subcategoria && subcategoria !== 'Todas') params.set('subcategoria', subcategoria);
    params.set('canal', 'app');
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    return request(`/productos?${params}`);
  },
  getProducto: (codigo) => request(`/productos/${encodeURIComponent(codigo)}?canal=app`),
  extraerNombre: (codigo) =>
    request(`/productos/${encodeURIComponent(codigo)}/extraer-nombre`, { method: 'POST' }),
  getClientes: (q = '') => request(`/clientes?q=${encodeURIComponent(q)}&limit=200`),
  getSucursales: (qOrOpts = '') => {
    const opts =
      typeof qOrOpts === 'string'
        ? { q: qOrOpts, limit: 80 }
        : { limit: 80, ...qOrOpts };
    return request(`/sucursales${vendorQuery(opts)}`);
  },
  getSucursal: (id) => request(`/sucursales/${id}`),
  crearSucursal: (body) =>
    request(`/sucursales${vendorQuery()}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSedes: (clienteId) => request(`/sucursales/${clienteId}/sedes`),
  crearSede: (clienteId, body) =>
    request(`/sucursales/${clienteId}/sedes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  actualizarSede: (clienteId, sedeId, body) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  eliminarSede: (clienteId, sedeId) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, { method: 'DELETE' }),
  iniciarVisita: (body) => request('/visitas', { method: 'POST', body: JSON.stringify(body) }),
  registrarPuntoVisita: (id, body) =>
    request(`/visitas/${id}/punto`, { method: 'POST', body: JSON.stringify(body) }),
  cerrarVisita: (id, body) =>
    request(`/visitas/${id}/cerrar`, { method: 'PATCH', body: JSON.stringify(body) }),
  enviarUbicacionVendedor: (body) =>
    request('/vendedor/ubicacion', { method: 'POST', body: JSON.stringify(body) }),
  /** Soft-offline (logout). Preferir beaconUbicacionOffline en pagehide. */
  marcarUbicacionOffline: () =>
    request('/vendedor/ubicacion/offline', { method: 'POST', body: '{}' }),
  /**
   * Cierre de pestaña web: keepalive / sendBeacon para limpiar señal en vivo.
   * No bloquea el unload; el admin deja de ver "en campo" (~inmediato o vía TTL).
   */
  beaconUbicacionOffline: () => {
    const token = getAuthToken();
    if (!token) return false;
    const base = `${API_BASE || ''}/api/vendedor/ubicacion/offline`;
    try {
      if (typeof fetch === 'function') {
        fetch(base, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: '{}',
          keepalive: true,
        }).catch(() => {});
        return true;
      }
    } catch {
      /* fall through */
    }
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const url = `${base}?token=${encodeURIComponent(token)}`;
        return navigator.sendBeacon(url, new Blob(['{}'], { type: 'application/json' }));
      }
    } catch {
      /* ignore */
    }
    return false;
  },
  getMisVisitas: (opts = {}) => request(`/vendedor/visitas${vendorQuery(opts)}`),
  cancelarVisitaActiva: (body = {}) =>
    request('/vendedor/visitas/cancelar', { method: 'POST', body: JSON.stringify(body) }),
  getCliente: (id) => request(`/clientes/${id}`),
  crearCliente: (body) => request('/clientes', { method: 'POST', body: JSON.stringify(body) }),
  actualizarCliente: (id, body) =>
    request(`/clientes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eliminarCliente: (id) => request(`/clientes/${id}`, { method: 'DELETE' }),
  getVendedores: () => request('/vendedores'),
  crearVendedor: (body) => request('/vendedores', { method: 'POST', body: JSON.stringify(body) }),
  actualizarVendedor: (id, body) =>
    request(`/vendedores/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eliminarVendedor: (id) => request(`/vendedores/${id}`, { method: 'DELETE' }),
  getPedidos: (estado = '') => request(`/pedidos${vendorQuery({ estado: estado || undefined })}`),
  getPedido: (id) => request(`/pedidos/${id}`),
  crearPedido: (body) =>
    request('/pedidos', { method: 'POST', body: JSON.stringify(body) }),
  syncCartReserva: (body) =>
    request('/carrito/reserva', { method: 'POST', body: JSON.stringify(body) }),
  clearCartReserva: (sessionId) =>
    request(`/carrito/reserva?session_id=${encodeURIComponent(sessionId || '')}`, {
      method: 'DELETE',
    }),
  actualizarEstado: (id, estado) =>
    request(`/pedidos/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({ estado }),
    }),
};
